import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import LandingPage from './views/LandingPage';
import LegalPage from './views/LegalPage';
import { parseWebRoute } from './lib/webRoutes';
import './index.css';

// The browser pages (/setup, /operator, /menu, /home, /<company>/…) pull in
// MSAL (~230 KB). Lazy-load so marketing-page visitors don't pay for it.
const SetupPage = lazy(() => import('./views/SetupPage'));
const OperatorPage = lazy(() => import('./views/OperatorPage'));
const PhoneMenuPage = lazy(() => import('./views/PhoneMenuPage'));
const TenantPages = lazy(() => import('./views/TenantPages'));
const AuthCallbackPage = lazy(() => import('./views/AuthCallbackPage'));

function lazyPage(node: React.ReactNode) {
  return <Suspense fallback={<div className="min-h-screen bg-gray-50" />}>{node}</Suspense>;
}

// Path-based routing without a router dependency:
//   /setup               → browser-based bootstrap wizard (MSAL.js, no Teams)
//   /operator            → tenant access management for the operator (MSAL.js)
//   /auth                → MSAL redirect + post-logout callback for the pages below
//   /<company>           → public, branded sign-in page of one tenant
//   /<company>/home      → signed-in hub;  /<company>/home/app → the directory
//   /<company>/menu[/id] → the phone-menu guide
//   /home, /home/app, /menu[/id] → the same without the company prefix
//                          (sign in, then continue at the company address)
//   /app, /app/*         → the Teams-embedded app (uses Teams SSO)
//   /privacy, /terms     → legal pages (manifest URLs point here)
//   anything else        → the public marketing page
function pickRoot() {
  const p = window.location.pathname;
  if (p === '/setup' || p.startsWith('/setup/')) return lazyPage(<SetupPage />);
  if (p === '/operator' || p.startsWith('/operator/')) return lazyPage(<OperatorPage />);
  if (p === '/app' || p.startsWith('/app/')) return <App />;
  if (p === '/privacy') return <LegalPage variant="privacy" />;
  if (p === '/terms') return <LegalPage variant="terms" />;
  const route = parseWebRoute(p);
  if (route.page === 'auth') return lazyPage(<AuthCallbackPage />);
  if (route.page === 'menu') return lazyPage(<PhoneMenuPage />);
  if (route.page) return lazyPage(<TenantPages />);
  return <LandingPage />;
}

// Self-heal stale lazy-chunk loads. After a deploy, a long-lived client (e.g. a
// Teams tab held open across a release) still references the OLD content-hashed
// chunk filenames (dagre, SetupPage, OperatorPage); those 404 once the deploy
// swaps assets out. Vite fires 'vite:preloadError' on such failures — reload
// once to pull the fresh bundle. The sessionStorage guard prevents a reload loop
// if a chunk is genuinely gone.
window.addEventListener('vite:preloadError', () => {
  if (!sessionStorage.getItem('chunkReloaded')) {
    sessionStorage.setItem('chunkReloaded', '1');
    window.location.reload();
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {pickRoot()}
  </React.StrictMode>
);
